import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Clapperboard, Upload, X, Copy, Check, ArrowLeft, Trash2, Share2 } from "lucide-react";
import { OS_API, osGet, osHeaders, t } from "./osApi";

const AUDIT_COLORS = { food: "#D4A574", coffee: "#C0604A", people: "#6FA8C9", story: "#C08FBF", interior: "#8FBF9F", bts: "#E0B84C", owner: "#7BA05B", lifestyle: "#8A8178" };
const AUDIT_CN = { food: "食物", coffee: "咖啡", people: "人物", story: "故事", interior: "空间", bts: "幕后", owner: "主理人", lifestyle: "生活方式" };

function CopyBtn({ text, testid }) {
    const [ok, setOk] = useState(false);
    return (
        <button data-testid={testid} onClick={async () => { try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 2000); } catch { /* noop */ } }}
            className="shrink-0 text-[#F5F0E8]/40 hover:text-[#D4A574] transition-colors">
            {ok ? <Check size={14} className="text-[#7BA05B]" /> : <Copy size={14} />}
        </button>
    );
}

function Label({ en, cn, lang, color = "#D4A574" }) {
    return <p className="text-[10px] uppercase tracking-[0.26em] font-bold mb-3" style={{ color }}>{t(lang, en, cn)}</p>;
}

function DnaRow({ label, val }) {
    if (!val) return null;
    return (
        <div className="flex gap-5 text-[13px] leading-relaxed border-b border-white/6 pb-3">
            <span className="w-24 shrink-0 text-[10.5px] uppercase tracking-[0.14em] text-[#F5F0E8]/40 pt-0.5">{label}</span>
            <span className="text-[#F5F0E8]/80">{val}</span>
        </div>
    );
}

function CopyBlock({ title, text, testid }) {
    if (!text) return null;
    return (
        <div className="rounded-xl bg-white/4 border border-white/8 p-4">
            <div className="flex justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#F5F0E8]/40 font-bold mb-1.5">{title}</p>
                    <p className="text-[12.5px] text-[#F5F0E8]/75 leading-relaxed whitespace-pre-wrap">{text}</p>
                </div>
                <CopyBtn text={text} testid={testid} />
            </div>
        </div>
    );
}

function ShareBtn({ packId, lang }) {
    const [busy, setBusy] = useState(false);
    const [ok, setOk] = useState(false);
    const share = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`${OS_API}/os/director/packs/${packId}/share`, null, { headers: osHeaders() });
            await navigator.clipboard.writeText(`${window.location.origin}${r.data.path}`);
            setOk(true); setTimeout(() => setOk(false), 2500);
        } catch { /* noop */ } finally { setBusy(false); }
    };
    return (
        <button onClick={share} disabled={busy} data-testid="os-pack-share"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#6FA8C9]/50 text-[#6FA8C9] px-4 py-1.5 text-[10.5px] uppercase tracking-[0.12em] font-bold hover:bg-[#6FA8C9]/12 transition-colors">
            {busy ? <Loader2 size={11} className="animate-spin" /> : ok ? <Check size={11} /> : <Share2 size={11} />}
            {ok ? t(lang, "Link copied!", "链接已复制!") : t(lang, "Share with client", "分享给客户")}
        </button>
    );
}

export function PackView({ pack, lang, onBack, onDelete, shared }) {
    const dna = pack.brand_dna || {};
    const st = pack.strategy || {};
    const mix = (pack.content_audit?.mix || []).filter((m) => m.pct > 0);
    return (
        <div className="max-w-3xl mx-auto space-y-12" data-testid="os-director-pack">
            {!shared && (
                <div className="flex items-center justify-between">
                    <button onClick={onBack} data-testid="os-pack-back" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-[#F5F0E8]/50 hover:text-[#D4A574]"><ArrowLeft size={13} /> {t(lang, "All packs", "全部 Pack")}</button>
                    <div className="flex items-center gap-3">
                        <ShareBtn packId={pack.id} lang={lang} />
                        <button onClick={() => onDelete(pack.id)} data-testid="os-pack-delete" className="text-[#F5F0E8]/30 hover:text-[#C0604A]"><Trash2 size={14} /></button>
                    </div>
                </div>
            )}

            <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4A574] font-bold">Director Pack</p>
                <h2 className="font-serif text-4xl sm:text-5xl mt-3" data-testid="os-pack-brand">{pack.brand_name}</h2>
                {(pack.director_notes_en || pack.director_notes_cn) && (
                    <p className="mt-4 text-[13.5px] leading-relaxed text-[#F5F0E8]/65 italic max-w-xl">“{t(lang, pack.director_notes_en, pack.director_notes_cn)}”</p>
                )}
                {(pack.images || []).length > 0 && (
                    <div className="flex gap-2 mt-5">
                        {pack.images.slice(0, 6).map((u, i) => <img key={i} src={u} alt="" className="h-16 w-16 rounded-lg object-cover" />)}
                    </div>
                )}
            </div>

            <section>
                <Label en="Brand DNA" cn="品牌 DNA" lang={lang} />
                <div className="space-y-3">
                    <DnaRow label={t(lang, "Personality", "个性")} val={t(lang, dna.personality_en, dna.personality_cn)} />
                    <DnaRow label={t(lang, "Audience", "受众")} val={t(lang, dna.audience_en, dna.audience_cn)} />
                    <DnaRow label={t(lang, "Positioning", "定位")} val={t(lang, dna.positioning_en, dna.positioning_cn)} />
                    <DnaRow label={t(lang, "Visual identity", "视觉识别")} val={t(lang, dna.visual_identity_en, dna.visual_identity_cn)} />
                    <DnaRow label={t(lang, "Emotion", "情绪")} val={t(lang, dna.emotion_en, dna.emotion_cn)} />
                    <DnaRow label={t(lang, "Psychology", "顾客心理")} val={t(lang, dna.psychology_en, dna.psychology_cn)} />
                </div>
            </section>

            <section data-testid="os-pack-audit">
                <Label en="Content Audit" cn="内容审计" lang={lang} color="#6FA8C9" />
                <div className="space-y-2">
                    {mix.map((m) => (
                        <div key={m.category} className="flex items-center gap-3">
                            <span className="w-20 shrink-0 text-[11px] text-[#F5F0E8]/55">{lang === "cn" ? (AUDIT_CN[m.category] || m.category) : m.category}</span>
                            <div className="flex-1 h-1.5 rounded-full bg-white/6"><div className="h-full rounded-full" style={{ width: `${m.pct}%`, background: AUDIT_COLORS[m.category] || "#D4A574" }} /></div>
                            <span className="w-9 text-right text-[11.5px] text-[#F5F0E8]/60">{m.pct}%</span>
                        </div>
                    ))}
                </div>
                <div className="mt-4 space-y-1.5">
                    {((lang === "cn" ? pack.content_audit?.missing_cn : pack.content_audit?.missing_en) || []).map((m, i) => (
                        <p key={i} className="text-[12.5px] text-[#C0604A]/90">✕ {m}</p>
                    ))}
                </div>
            </section>

            <section>
                <Label en="Creative Opportunities" cn="创意机会" lang={lang} color="#7BA05B" />
                <div className="space-y-4">
                    {(pack.opportunities || []).map((o, i) => (
                        <div key={i} className="border-l-2 border-[#7BA05B]/50 pl-4">
                            <p className="text-[14px] font-bold">{t(lang, o.title_en, o.title_cn)}</p>
                            <p className="text-[12.5px] text-[#F5F0E8]/60 leading-relaxed mt-1">{t(lang, o.why_en, o.why_cn)}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <Label en="Director Strategy" cn="导演策略" lang={lang} color="#C08FBF" />
                <p className="font-serif text-xl leading-snug max-w-xl">{t(lang, st.main_story_en, st.main_story_cn)}</p>
                <div className="mt-5 space-y-3">
                    <DnaRow label={t(lang, "Opening hook", "开场钩子")} val={t(lang, st.hook_en, st.hook_cn)} />
                    <DnaRow label={t(lang, "Visual mood", "视觉情绪")} val={t(lang, st.mood_en, st.mood_cn)} />
                    <DnaRow label={t(lang, "Music", "音乐方向")} val={t(lang, st.music_en, st.music_cn)} />
                    <DnaRow label={t(lang, "Camera style", "运镜风格")} val={t(lang, st.camera_style_en, st.camera_style_cn)} />
                    <DnaRow label={t(lang, "Editing rhythm", "剪辑节奏")} val={t(lang, st.editing_en, st.editing_cn)} />
                    <DnaRow label={t(lang, "Ending emotion", "结尾情绪")} val={t(lang, st.ending_en, st.ending_cn)} />
                </div>
                {(st.palette || []).length > 0 && (
                    <div className="flex gap-2 mt-4">
                        {st.palette.map((c, i) => <span key={i} className="h-8 w-8 rounded-full border border-white/15" style={{ background: c }} title={c} />)}
                    </div>
                )}
            </section>

            <section data-testid="os-pack-shotlist">
                <Label en="Shot List" cn="Shot List · 分镜清单" lang={lang} color="#E0B84C" />
                <div className="space-y-4">
                    {(pack.shot_list || []).map((s) => (
                        <div key={s.no} className="rounded-xl border border-white/8 bg-white/3 p-4">
                            <div className="flex items-baseline gap-3 flex-wrap">
                                <span className="font-serif text-lg text-[#E0B84C]">{String(s.no).padStart(2, "0")}</span>
                                <p className="text-[13.5px] font-bold flex-1">{t(lang, s.shot_en, s.shot_cn)}</p>
                                <span className="text-[10px] uppercase tracking-[0.14em] text-[#F5F0E8]/40">{s.type} · {s.frame} · {s.lens}</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-1 mt-2.5 text-[11.5px] text-[#F5F0E8]/60 leading-relaxed">
                                <p>🎥 {t(lang, s.motion_en, s.motion_cn)}</p>
                                <p>💡 {t(lang, s.lighting_en, s.lighting_cn)}</p>
                                <p>🎙 {t(lang, s.audio_en, s.audio_cn)}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {(pack.reel_script?.beats || []).length > 0 && (
                <section data-testid="os-pack-storyboard">
                    <Label en={`Reel Script · ${pack.reel_script.duration_s || 30}s`} cn={`Reel 脚本 · ${pack.reel_script.duration_s || 30} 秒`} lang={lang} color="#6FA8C9" />
                    {(pack.reel_script.title_en || pack.reel_script.title_cn) && (
                        <p className="font-serif text-xl mb-4">{t(lang, pack.reel_script.title_en, pack.reel_script.title_cn)}</p>
                    )}
                    <div className="space-y-2.5">
                        {pack.reel_script.beats.map((b, i) => (
                            <div key={i} className="flex gap-4 rounded-xl border border-white/8 bg-white/3 p-3.5">
                                <span className="shrink-0 w-14 text-[11px] font-bold text-[#6FA8C9] pt-0.5">{b.time}</span>
                                <div className="min-w-0 flex-1">
                                    <p className="text-[13px] text-[#F5F0E8]/85 leading-relaxed">{t(lang, b.visual_en, b.visual_cn)}</p>
                                    {(b.overlay_en || b.overlay_cn) && <p className="text-[11.5px] text-[#E0B84C]/85 mt-1">💬 {t(lang, b.overlay_en, b.overlay_cn)}</p>}
                                    {(b.audio_en || b.audio_cn) && <p className="text-[11.5px] text-[#F5F0E8]/45 mt-0.5">🎵 {t(lang, b.audio_en, b.audio_cn)}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section data-testid="os-pack-checklist">
                <Label en="Live Shoot Checklist" cn="现场拍摄清单" lang={lang} color="#C0604A" />
                <div className="space-y-2">
                    {((lang === "cn" ? pack.checklist_cn : pack.checklist_en) || []).map((c, i) => (
                        <p key={i} className="text-[13px] text-[#F5F0E8]/75 flex gap-2.5"><span className="text-[#C0604A]">□</span> {c}</p>
                    ))}
                </div>
            </section>

            <section className="space-y-4" data-testid="os-pack-captions">
                <Label en={shared ? "Captions" : "Captions & Pitch"} cn={shared ? "文案" : "文案与提案"} lang={lang} />
                <CopyBlock title={t(lang, "Commercial caption", "商业 Caption")} text={t(lang, pack.captions?.commercial_en, pack.captions?.commercial_cn)} testid="os-copy-commercial" />
                <CopyBlock title={t(lang, "Slow story caption", "Slow Story 文案")} text={t(lang, pack.captions?.slow_story_en, pack.captions?.slow_story_cn)} testid="os-copy-slowstory" />
                {!shared && <CopyBlock title={t(lang, "IG DM pitch", "IG 私讯提案")} text={t(lang, pack.pitch?.dm_en, pack.pitch?.dm_cn)} testid="os-copy-dm" />}
                {!shared && <CopyBlock title="Email" text={pack.pitch?.email_en} testid="os-copy-email" />}
                {!shared && <CopyBlock title="WhatsApp" text={pack.pitch?.whatsapp_en} testid="os-copy-whatsapp" />}
            </section>
        </div>
    );
}

const STEP_LABELS = {
    image_upload: ["Uploading assets", "正在上传素材"],
    image_vision_analysis: ["Analysing brand visuals", "正在分析品牌视觉"],
    instagram_brand_context: ["Building brand context", "正在整理品牌背景"],
    brand_dna: ["Defining Brand DNA", "正在提炼品牌 DNA"],
    creative_direction: ["Writing director strategy", "正在生成导演方案"],
    shot_list: ["Building the shot list", "正在整理 Shot List"],
    storyboard: ["Scripting the reel", "正在编写 Reel 脚本"],
    client_pitch: ["Writing the client pitch", "正在生成客户提案"],
    final_director_pack: ["Assembling Director Pack", "正在汇整 Director Pack"],
};

function JobProgress({ job, lang }) {
    if (!job) return null;
    const pct = job.progress || 5;
    const label = STEP_LABELS[job.current_step] || STEP_LABELS.image_upload;
    const eta = Math.max(5, Math.round((100 - pct) * 1.5));
    return (
        <div className="rounded-xl border border-[#D4A574]/30 bg-[#D4A574]/6 p-5" data-testid="os-director-progress">
            <div className="flex items-center justify-between text-[11.5px]">
                <span className="inline-flex items-center gap-2 text-[#D4A574] font-bold uppercase tracking-[0.14em]">
                    <Loader2 size={12} className="animate-spin" /> <span data-testid="os-director-step">{t(lang, label[0], label[1])}</span>
                </span>
                <span className="text-[#F5F0E8]/45">{t(lang, `~${eta}s left`, `预计还需 ${eta} 秒`)}</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-white/8 overflow-hidden">
                <div className="h-full rounded-full bg-[#D4A574] transition-all duration-700" style={{ width: `${pct}%` }} data-testid="os-director-progress-bar" />
            </div>
            <p className="mt-2 text-right text-[11px] text-[#D4A574] font-bold">{pct}%</p>
        </div>
    );
}

export default function OSDirector({ lang, onProgress }) {
    const [packs, setPacks] = useState([]);
    const [selected, setSelected] = useState(null);
    const [files, setFiles] = useState([]);
    const [brandName, setBrandName] = useState("");
    const [links, setLinks] = useState("");
    const [busy, setBusy] = useState(false);
    const [job, setJob] = useState(null);
    const [err, setErr] = useState("");
    const [retryId, setRetryId] = useState(null);

    const loadPacks = useCallback(async () => {
        try { const r = await osGet("/os/director/packs"); setPacks(r.data); return r.data; } catch { return null; }
    }, []);
    useEffect(() => { loadPacks(); }, [loadPacks]);

    // poll an active job every 3s until ready/error — survives page refresh
    // because generating packs re-appear in the library and resume polling
    const pollJob = useCallback(async (id) => {
        setBusy(true); setErr(""); setRetryId(null);
        try {
            for (let i = 0; i < 200; i++) {
                let pr;
                try { pr = await osGet(`/os/director/packs/${id}`); }
                catch { await new Promise((r) => setTimeout(r, 3000)); continue; }
                const d = pr.data;
                if (d.status === "ready" || (!d.status && d.brand_dna)) {
                    setJob(null); setSelected(d);
                    setFiles([]); setBrandName(""); setLinks("");
                    loadPacks(); onProgress && onProgress();
                    return;
                }
                if (d.status === "error") {
                    setJob(null); setRetryId(id);
                    setErr(d.error || t(lang, "Generation failed", "生成失败"));
                    loadPacks();
                    return;
                }
                setJob({ id, current_step: d.current_step, progress: d.progress });
                await new Promise((r) => setTimeout(r, 3000));
            }
            setErr(t(lang, "Taking too long — check the library later.", "耗时过长 — 稍后在资料库查看。"));
        } finally { setBusy(false); }
    }, [lang, loadPacks, onProgress]);

    // after a refresh: resume watching any pack that's still generating
    useEffect(() => {
        const gen = packs.find((p) => p.status === "generating");
        if (gen && !busy && !selected) pollJob(gen.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packs.length]);

    const addFiles = (list) => setFiles([...files, ...Array.from(list)].slice(0, 6));

    const generate = async () => {
        setBusy(true); setErr(""); setJob({ id: null, current_step: "image_upload", progress: 3 });
        try {
            const fd = new FormData();
            files.forEach((f) => fd.append("files", f));
            fd.append("brand_name", brandName);
            fd.append("links", links);
            const r = await axios.post(`${OS_API}/os/director/pack`, fd, { headers: osHeaders(), timeout: 120000 });
            loadPacks();
            await pollJob(r.data.id);
        } catch (e) {
            setJob(null); setBusy(false);
            setErr(e?.response?.data?.detail || t(lang, "Upload failed — try again.", "上传失败,请重试。"));
        }
    };

    const retry = async (id) => {
        setErr(""); setRetryId(null);
        try {
            await axios.post(`${OS_API}/os/director/packs/${id}/retry`, null, { headers: osHeaders() });
            await pollJob(id);
        } catch { setErr(t(lang, "Retry failed", "重试失败")); setRetryId(id); }
    };

    const remove = async (id) => {
        try { await axios.delete(`${OS_API}/os/director/packs/${id}`, { headers: osHeaders() }); } catch { /* noop */ }
        setSelected(null); loadPacks();
    };

    const openPack = async (id) => {
        try {
            const r = await osGet(`/os/director/packs/${id}`);
            if (r.data.status === "ready" || (!r.data.status && r.data.brand_dna)) setSelected(r.data);
            else if (r.data.status === "error") setRetryId(id);
        } catch { /* noop */ }
    };

    if (selected) return <PackView pack={selected} lang={lang} onBack={() => setSelected(null)} onDelete={remove} />;

    return (
        <div className="max-w-3xl mx-auto space-y-14" data-testid="os-director">
            <div>
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4A574] font-bold inline-flex items-center gap-1.5"><Clapperboard size={13} /> {t(lang, "Creative Lab", "创意实验室")}</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">{t(lang, "Your judgment first.", "先写下你的判断。")}<br />{t(lang, "AI feedback second — on manual request.", "手动点按钮后，再请 AI 给反馈。")}</h1>
                <p className="mt-4 text-[13.5px] text-[#F5F0E8]/55 leading-relaxed max-w-xl">
                    {t(lang,
                        "Upload 1-6 reference screenshots of a brand (IG, menu, space). Write what YOU would do first — brand DNA guess, concept, audience, shot list. Then tap 'Request AI feedback' to compare your judgment with the model's. AI is strictly on-demand — this page never fires paid calls automatically.",
                        "上传 1-6 张品牌参考（IG / 菜单 / 空间）。先写下你自己的判断 — 品牌 DNA 直觉、概念、受众、Shot List。然后点击'请求 AI 反馈'，看看 AI 对你的判断。本页不会自动触发付费 AI 调用。")}
                </p>
                <p className="mt-3 text-[11px] text-[#F5F0E8]/40">
                    {t(lang,
                        "Each 'Build' click uses the Emergent Universal Key · estimated ≈ USD 0.02 per pack.",
                        "每次'生成'调用 Emergent Universal Key · 每份 Pack 约 USD 0.02。")}
                </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/4 p-6 space-y-5">
                {/* Kaily-first scratchpad — written before any AI call */}
                <div className="rounded-xl border border-[#7BA05B]/30 bg-[#7BA05B]/5 p-4" data-testid="os-creative-lab-judgment">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#7BA05B] font-bold mb-2">
                        {t(lang, "1. Kaily's judgment — write first", "1. 先写：Kaily 自己的判断")}
                    </p>
                    <textarea rows={5} placeholder={t(lang,
                        "Brand DNA guess · Audience · One-line concept · Shot list · Lighting / sound notes…",
                        "品牌 DNA 直觉 · 受众 · 一句话概念 · Shot list · 灯光 / 声音备注…")}
                        className="w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-[12.5px] focus:outline-none focus:border-[#7BA05B] resize-none text-[#F5F0E8]"
                        data-testid="os-creative-lab-notes"
                        onChange={(e) => { try { localStorage.setItem("os-creative-lab-notes", e.target.value); } catch { /* noop */ } }}
                        defaultValue={(() => { try { return localStorage.getItem("os-creative-lab-notes") || ""; } catch { return ""; } })()} />
                    <p className="mt-1.5 text-[10px] text-[#F5F0E8]/35">
                        {t(lang, "Saved in your browser. Private. AI never overwrites this.",
                            "仅存你的浏览器。AI 不会覆盖你写的内容。")}
                    </p>
                </div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4A574] font-bold">
                    {t(lang, "2. Then request AI feedback below", "2. 然后再请求 AI 反馈 ↓")}
                </p>
                <label className="block cursor-pointer rounded-xl border-2 border-dashed border-white/15 hover:border-[#D4A574]/60 transition-colors p-8 text-center" data-testid="os-director-dropzone">
                    <input type="file" accept="image/*" multiple className="hidden" data-testid="os-director-file-input"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    <Upload size={20} className="mx-auto text-[#F5F0E8]/40" />
                    <p className="mt-2.5 text-[12.5px] text-[#F5F0E8]/55">{t(lang, "Drop brand screenshots here (1-6)", "上传品牌截图(1-6 张)")}</p>
                </label>
                {files.length > 0 && (
                    <div className="flex gap-2.5 flex-wrap" data-testid="os-director-previews">
                        {files.map((f, i) => (
                            <div key={i} className="relative">
                                <img src={URL.createObjectURL(f)} alt="" className="h-16 w-16 rounded-lg object-cover" />
                                <button onClick={() => setFiles(files.filter((_, j) => j !== i))}
                                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-[#C0604A] text-white flex items-center justify-center"><X size={10} /></button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input value={brandName} onChange={(e) => setBrandName(e.target.value)} data-testid="os-director-brand-input"
                        placeholder={t(lang, "Brand name (optional)", "品牌名(可选)")}
                        className="rounded-full bg-white/6 border border-white/12 px-4 py-2.5 text-[12.5px] focus:outline-none focus:border-[#D4A574]" />
                    <input value={links} onChange={(e) => setLinks(e.target.value)} data-testid="os-director-links-input"
                        placeholder={t(lang, "IG / Maps link (optional)", "IG / 地图链接(可选)")}
                        className="rounded-full bg-white/6 border border-white/12 px-4 py-2.5 text-[12.5px] focus:outline-none focus:border-[#D4A574]" />
                </div>
                {busy && job ? (
                    <JobProgress job={job} lang={lang} />
                ) : (
                    <button onClick={generate} disabled={busy || files.length === 0} data-testid="os-director-generate"
                        className="w-full rounded-full bg-[#D4A574] text-[#0D0A08] py-3.5 text-[12px] uppercase tracking-[0.2em] font-bold hover:bg-[#F5F0E8] transition-colors disabled:opacity-40">
                        {t(lang, "Request AI feedback (manual · ≈ USD 0.02)", "请求 AI 反馈（手动触发 · 约 USD 0.02）")}
                    </button>
                )}
                {err && (
                    <div className="text-center space-y-3" data-testid="os-director-error">
                        <p className="text-[12px] text-[#C0604A]">{err}</p>
                        {retryId && (
                            <button onClick={() => retry(retryId)} data-testid="os-director-retry"
                                className="rounded-full border border-[#C0604A]/50 text-[#C0604A] px-6 py-2 text-[11px] uppercase tracking-[0.14em] font-bold hover:bg-[#C0604A]/10 transition-colors">
                                {t(lang, "Regenerate (resumes from last step)", "重新生成(从上次步骤继续)")}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {packs.length > 0 && (
                <div data-testid="os-director-library">
                    <p className="text-[10px] uppercase tracking-[0.26em] text-[#F5F0E8]/45 font-bold mb-4">{t(lang, "Pack library", "Pack 资料库")} · {packs.length}</p>
                    <div className="space-y-3">
                        {packs.map((p) => (
                            <button key={p.id} onClick={() => openPack(p.id)} data-testid={`os-pack-item-${p.id}`}
                                className="w-full flex items-center gap-4 rounded-xl border border-white/8 bg-white/3 hover:border-[#D4A574]/50 transition-colors p-4 text-left">
                                {(p.images || [])[0] && <img src={p.images[0]} alt="" className="h-12 w-12 rounded-lg object-cover shrink-0" />}
                                <div className="min-w-0 flex-1">
                                    <p className="text-[14px] font-bold truncate">{p.brand_name}</p>
                                    <p className="text-[11.5px] text-[#F5F0E8]/45 truncate mt-0.5">
                                        {p.status === "generating"
                                            ? `${t(lang, (STEP_LABELS[p.current_step] || ["Directing…"])[0], (STEP_LABELS[p.current_step] || ["", "分析中…"])[1])} · ${p.progress || 0}%`
                                            : p.status === "error" ? t(lang, "Failed — tap to retry", "生成失败 — 点击重试")
                                                : t(lang, p.director_notes_en, p.director_notes_cn)}
                                    </p>
                                </div>
                                <span className="text-[10.5px] text-[#F5F0E8]/35 shrink-0">{(p.created_at || "").slice(0, 10)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
